import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    postMessageRN,
    runningInRNWebView,
    useReactNativeProps,
    WebToRNAction,
} from '@eika-infrastruktur/mobilbank-lib';
import { AttentionMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';
import { Modal } from '@eika/modal';

import { useSession } from '@/components/app';
import { Query } from '@/domain/app';
import { useCreditCardsList } from '@/domain/card';
import {
    ApplePayCardWithNativeState,
    AppleWalletService,
    PairedCreditCards,
    useEligibleCreditCardsQueryForApple,
} from '@/domain/digital-wallet';
import useApplePayEligibleCardQuery from '@/domain/digital-wallet/hooks/query/use-apple-pay-eligible-card-query';
import { getApplePayCardNativeState } from '@/domain/digital-wallet/utils/get-apple-pay-status';
import piwik from '@/piwik/piwik';
import { useMyDebitCards } from '@/react-query/hooks/query/use-debit-cards';
import CardSelect from '@/widgets/ApplePayProvision/CardSelect/CardSelect';
import useApplePayCookies from '@/widgets/ApplePayProvision/useApplePayCookies';

import addToAppleWallet from '@domain/digital-wallet/components/apple-pay/AddToAppleWallet.svg';
import applePay from '@domain/digital-wallet/components/apple-pay/ApplePay.svg';
import ApplePayModalContent from '@domain/digital-wallet/components/apple-pay/ApplePayModalContent';
import useHandleApplePayProvisioningEvent, {
    ProvisionHandle,
} from '@domain/digital-wallet/hooks/use-handle-apple-pay-provisioning-event';
import { Card, CreditCard, CreditCardWalletType } from '@generated-types';
import useText from '@hooks/use-text';

import './ApplePayProvision.scss';

type ApplePayOnboardingContentProps = {
    eligibleCardsList: ApplePayCardWithNativeState[];
    invalidateQuery(): Promise<void>;
    isLoading: boolean;
    provisionHandle: ProvisionHandle;
    pairedCreditCardsList: PairedCreditCards[];
};

const ApplePayOnboardingContent = ({
    isLoading,
    eligibleCardsList,
    provisionHandle,
    invalidateQuery,
    pairedCreditCardsList,
}: ApplePayOnboardingContentProps) => {
    const text = useText();
    const { status, cleanup, setIsPending } = provisionHandle;

    const allEligibleCardsOptions = useMemo(
        () => [
            ...eligibleCardsList.map(({ eligible, card }) => ({
                value: eligible.eikaCardId,
                title: eligible.title,
                cardNumber: card.number,
            })),
            ...pairedCreditCardsList.map((pair) => ({
                value: pair.card.id,
                title: pair.card.product.name || 'product',
                cardNumber: pair.card.cardNumber,
            })),
        ],
        [eligibleCardsList, pairedCreditCardsList],
    );

    const [selectedCard, setSelectedCard] = useState<string | undefined>(allEligibleCardsOptions[0].value);

    const selectCard = (cardId: string) => {
        setSelectedCard(cardId);
    };

    useEffect(
        () => setSelectedCard(allEligibleCardsOptions[0].value),
        [allEligibleCardsOptions, eligibleCardsList, setSelectedCard],
    );

    const onboardCard = useCallback(
        (selectedDigitalCard: ApplePayCardWithNativeState) => {
            const provisionPayload = AppleWalletService.toProvisioningPayload(selectedDigitalCard);
            setIsPending();
            postMessageRN({ action: WebToRNAction.APPLE_PAY_PROVISION, payload: provisionPayload });
        },
        [setIsPending],
    );

    const onboardKbCard = useCallback(
        (selectedPair: PairedCreditCards) => {
            const provisionPayload = AppleWalletService.pairToProvisioningPayload(selectedPair);
            setIsPending();
            postMessageRN({ action: WebToRNAction.APPLE_PAY_PROVISION, payload: provisionPayload });
        },
        [setIsPending],
    );

    const [open, setOpen] = useState(true);

    const cleanupAndInvalidate = useCallback(() => {
        invalidateQuery();
        cleanup();
    }, [cleanup, invalidateQuery]);

    const { setCookies } = useApplePayCookies();

    const setCookie = useCallback(() => {
        setCookies(status !== 'initial');
        cleanupAndInvalidate();
    }, [status, setCookies, cleanupAndInvalidate]);

    const startOnboarding = useCallback(() => {
        if (!selectedCard) return;
        const teCard = eligibleCardsList.find((item) => item.eligible.eikaCardId === selectedCard);
        if (teCard) {
            onboardCard(teCard);
        }
        const kbCard = pairedCreditCardsList.find((pair) => pair.card.id === selectedCard);
        if (kbCard) {
            onboardKbCard(kbCard);
        }
    }, [eligibleCardsList, onboardCard, onboardKbCard, pairedCreditCardsList, selectedCard]);

    const selectedOption = allEligibleCardsOptions.find((option) => option.value === selectedCard);

    return (
        <Modal appNamespace="kortbetjening-web" open={open} setOpen={setOpen} onClose={setCookie}>
            <ApplePayModalContent
                isError={status === 'error'}
                isSuccess={status === 'success'}
                retry={cleanup}
                done={() => setOpen(false)}
            >
                <section className="apple-pay-modal__content-wrapper" data-testid="apple-pay-initial-message">
                    <InfoCard skipBackground className="apple-pay-modal__info-card">
                        <InfoCard.Image src={applePay} alt={text.applePay} />
                        <InfoCard.TitleH3>{text.applePayBankInfo}</InfoCard.TitleH3>
                    </InfoCard>
                    <div className="apple-pay-modal__card-select-wrapper">
                        <CardSelect
                            label={text.chooseCardToAddToAppleWallet}
                            options={allEligibleCardsOptions}
                            onChange={(value) => selectCard(value)}
                            selected={selectedOption}
                            isLoading={isLoading}
                            emptyMessage={text.onlyOneCardToAddToAppleWallet}
                        />

                        <div className="apple-pay-modal__submit-buttons">
                            {status === 'pending' ? (
                                <div className="apple-pay-button" data-testid="onboarding-pending">
                                    <Loader />
                                </div>
                            ) : (
                                <button
                                    className="apple-pay-button"
                                    data-testid="onboard-card"
                                    data-track-id={piwik.applePay.addToAppleWalletModal.dataTrackId}
                                    data-track-linkname={piwik.applePay.addToAppleWalletModal.dataTrackLinkname}
                                    type="button"
                                    onClick={startOnboarding}
                                >
                                    <img src={addToAppleWallet} alt="" />
                                    <span className="screen-reader-text">{text.addToAppleWallet}</span>
                                </button>
                            )}
                            <LinkButton
                                data-track-id={piwik.applePay.appleWalletModalNotNow.dataTrackId}
                                data-track-linkname={piwik.applePay.appleWalletModalNotNow.dataTrackLinkname}
                                onClick={() => setOpen(false)}
                            >
                                {text.notNow}
                            </LinkButton>
                        </div>
                        <AttentionMessage
                            icon={IconTypes.UTROPSTEGN_24}
                            className="apple-pay-modal__info-message"
                            type="default"
                            text={text.findApplePayAtCardPage}
                        />
                    </div>
                </section>
            </ApplePayModalContent>
        </Modal>
    );
};

type ApplePayOnboardingStateLoaderProps = {
    debitCards: Card[];
    creditCards: Card[];
    v4CreditCards: CreditCard[];
    provisionHandle: ProvisionHandle;
};

const ApplePayOnboardingStateLoader = ({
    debitCards,
    creditCards,
    v4CreditCards,
    provisionHandle,
}: ApplePayOnboardingStateLoaderProps) => {
    const cards = useMemo(() => [...debitCards, ...creditCards], [debitCards, creditCards]);
    const nativeProps = useReactNativeProps();

    const { status } = provisionHandle;

    const operationAllowed = useMemo(() => cards.some((card) => card.allowedOperations.setWallets), [cards]);
    const eligibleCardsQuery = useApplePayEligibleCardQuery(nativeProps.appleWallet, operationAllowed, true);

    const eligibleCardsQueryKb = useEligibleCreditCardsQueryForApple({
        select: (data) => data.filter((card) => card.walletType === CreditCardWalletType.APPLE_PAY),
    });

    const { showModal } = useApplePayCookies();

    if (
        eligibleCardsQueryKb.isPending ||
        eligibleCardsQueryKb.isError ||
        eligibleCardsQuery.isPending ||
        eligibleCardsQuery.isError
    ) {
        return null;
    }

    if ((eligibleCardsQuery.isPending || eligibleCardsQuery.isError) && status === 'initial') return null;

    const eligibleCardsList: ApplePayCardWithNativeState[] = AppleWalletService.pairCards(
        cards,
        eligibleCardsQuery.data,
    ).map((i) => ({
        ...i,
        nativeState: getApplePayCardNativeState(i.eligible, nativeProps, []),
    }));

    const hasOnboardedDevice = eligibleCardsList.some(({ nativeState }) => nativeState.cardIsInLocalWallet);
    const canOnboard = showModal && runningInRNWebView && eligibleCardsList.length > 0 && !hasOnboardedDevice;

    if (canOnboard || status !== 'initial') {
        const pairedCreditCardsList = AppleWalletService.pairCreditCards(
            v4CreditCards,
            eligibleCardsQueryKb.data ?? [],
        );

        return (
            <ApplePayOnboardingContent
                invalidateQuery={eligibleCardsQuery.invalidateQuery}
                eligibleCardsList={eligibleCardsList}
                isLoading={eligibleCardsQuery.isLoading || eligibleCardsQueryKb.isLoading}
                provisionHandle={provisionHandle}
                pairedCreditCardsList={pairedCreditCardsList}
            />
        );
    }
    return null;
};

type ProvisionQueryProps = {
    provisionHandle: ProvisionHandle;
};

const ApplePayProvisionQueryKb = ({ provisionHandle }: ProvisionQueryProps) => {
    const session = useSession();
    const debitEnabled = !!session.debitProfile;
    const creditEnabled = !!session.creditProfile;

    const debitCardsQuery = useMyDebitCards({ enabled: debitEnabled });
    const kredittbankCreditCardsQuery = useCreditCardsList({ enabled: creditEnabled });

    if (
        Query.isPendingOrError(kredittbankCreditCardsQuery, creditEnabled) ||
        Query.isPendingOrError(debitCardsQuery, debitEnabled)
    ) {
        return null;
    }

    return (
        <ApplePayOnboardingStateLoader
            creditCards={[]}
            debitCards={debitCardsQuery.data || []}
            provisionHandle={provisionHandle}
            v4CreditCards={kredittbankCreditCardsQuery.data || []}
        />
    );
};

const ApplePayProvision = () => {
    const nativeProps = useReactNativeProps();
    const provisionHandle = useHandleApplePayProvisioningEvent();

    const enabled = useMemo(
        () =>
            Boolean(nativeProps.supports?.applePay) &&
            Boolean(nativeProps.supports?.cookies) &&
            Boolean(nativeProps.appleWallet?.hasAvailableCardsForLocalOnboarding),
        [
            nativeProps.supports?.applePay,
            nativeProps.supports?.cookies,
            nativeProps.appleWallet?.hasAvailableCardsForLocalOnboarding,
        ],
    );

    if (!enabled && provisionHandle.status === 'initial') {
        return null;
    }

    return <ApplePayProvisionQueryKb provisionHandle={provisionHandle} />;
};

export default ApplePayProvision;
